import React, { ReactNode, useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import Twemoji from "react-twemoji";
import "overlayscrollbars/overlayscrollbars.css";

import { getBitmapPixelColor } from "client-util/image";
import { useEvent } from "client-util/useEvent";
import { AnswerNormalization } from "sockapi/answers";
import { ScopeType } from "sockapi/scope-types";
import { TeamPuzzleSockAPIScopeState } from "sockapi/scope-specs/team-spec";
import { getPuzzleContentStaticDir } from "sockapi/scope-specs/puzzle-content-spec";
import { CUSTOM_TITLE_MAX_LENGTH } from "sockapi/scope-specs/team-titles-spec";
import { ROUND_IDS, getPieceId } from "sockapi/scope-specs/round-spec";
import { PuzzleType, doesPieceBelongTo } from "sockapi/puzzle-data";

import {
  useTeamId,
  useShouldDisplayAdmin,
} from "stores/ServerInteractionStore";
import { useRound, useNavStore } from "stores/NavStore";
import {
  useScopeStateResolvable,
  useTeamScopeState,
  usePuzzleDisplayName,
  useIsPuzzleSolved,
  useGlobalScopeState,
  useCustomTitle,
} from "stores/ScopeStateStore";
import { useIsAdminEnabledStore } from "stores/UserPreferencesStore";
import { useBox, useBoxPieces } from "stores/RoundStore";
import { Spinner } from "components/Loading";
import { PuzzleContentDisplayPreloaded } from "components/PuzzleContent/PuzzleContentDisplay";
import { ErrataDisplay } from "pages/ErrataPage";
import AdminCannedHintsDisplay from "components/AdminQueue/AdminCannedHintsDisplay";
import { clsObj } from "client-util/util";
import { DESERT_ISLAND_ROUND_ID } from "components/PuzzleContent/IslandFinder";
import { PreviousGuessesDisplayPreloaded } from "components/PuzzlePage/PreviousGuessesDisplay";
import PieceOverlay from "components/Movables/PieceOverlay";
import SolutionBodyDisplay from "components/PuzzlePage/SolutionBodyDisplay";
import Tooltip from "components/Tooltip";
import { Link } from "components/Link";
import { LargeSpinner } from "components/Loading";
import { Paper } from "components/Material";
import { useRouteMatch } from "components/urls";
import { usePuzzleBodyDependencies } from "pages/PuzzlePage";
import { AdminPuzzleControl } from "pages/AdminPage";
import Globals from "Globals";
import settings from "settings";
import "./PuzzleBodyDisplay.css";
import { PieceMenu } from "components/Movables/PieceView";
import { SockAPIScopeState } from "sockapi/scope-state";
import { Approachability } from "components/Approachability";
import { BoxMenu } from "components/Movables/RoundView";

// Original default 18px font size multiplied by 2 for
// most common piece width.
export const BASE_FONT_SIZE_PX = 36;
const BASE_WRAPPER_BORDER_PX = 2;
export const BASE_OUTER_WRAPPER_WIDTH_PX = 900;
export const BASE_INNER_WRAPPER_WIDTH_PX =
  BASE_OUTER_WRAPPER_WIDTH_PX - 2 * BASE_WRAPPER_BORDER_PX;

export const FONT_SIZE_SCALING_PER_ROUND: { [roundId: string]: number } = {
  [ROUND_IDS.crosswords]: 16,
  [ROUND_IDS.endgame]: 0.275,
};

// GPH 2025: HACKKKKK
const TRUE_COLORS_PIECES_PUZ_NAMES = ["888df741", "e5f97436", "ad799386"];

function PuzzlePageTitleDisplay(props: {
  puzData: TeamPuzzleSockAPIScopeState;
  teamAnswerableScopeState?: SockAPIScopeState<ScopeType.TEAM_ANSWERABLE> | null;
}) {
  const { puzData, teamAnswerableScopeState } = props;
  const { puzName } = puzData;
  const teamId = useTeamId();
  const teamScopeState = useTeamScopeState();
  const displayName = usePuzzleDisplayName(puzName);
  const customTitles =
    useScopeStateResolvable(
      ScopeType.TEAM_TITLES,
      teamId !== null ? { teamId } : null
    )?.titles ?? null;
  const puzzleContent = useScopeStateResolvable(ScopeType.PUZZLE_CONTENT, {
    puzName,
  });
  const [isSettingCustomTitle, setIsSettingCustomTitle] = useState(false);
  const isSolved = useIsPuzzleSolved(puzName);
  const { approachability } = teamAnswerableScopeState ?? {};
  const customTitle = useCustomTitle(puzName, () =>
    setIsSettingCustomTitle(false)
  );

  if (
    teamId === null ||
    teamScopeState === null ||
    displayName === null ||
    customTitles === null ||
    puzzleContent === null
  )
    return <Spinner inline={true} />;

  const isDesert = puzData.roundId === DESERT_ISLAND_ROUND_ID;

  return (
    <>
      <div className="puzzle-header">
        {isSettingCustomTitle ? (
          <input
            type="text"
            autoFocus
            maxLength={CUSTOM_TITLE_MAX_LENGTH}
            defaultValue={customTitle.value}
            disabled={customTitle.disableInput}
            onBlur={(e) => customTitle.set(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                customTitle.set(e.currentTarget.value);
              }
            }}
          />
        ) : (
          <div className="puzzle-title">
            <h1>
              <Tooltip text={isSolved ? "Solved." : "Unsolved."}>
                <Twemoji>{isSolved ? "✅" : "🔲"}</Twemoji>
              </Tooltip>
              <Twemoji>{displayName} </Twemoji>
              {puzData.displayName === "" ? (
                <a
                  className="puzzle-rename-button"
                  href="#"
                  onClick={(evt) => {
                    evt.preventDefault();
                    setIsSettingCustomTitle(true);
                  }}
                >
                  <Tooltip
                    text={`${
                      customTitle.value ? "Rename" : "Name"
                    } this piece. This is just for your convenience and is shared with your team.`}
                    placement="bottom"
                  >
                    <Twemoji>✏️</Twemoji>
                  </Tooltip>
                </a>
              ) : null}
              {approachability !== null && approachability !== undefined && (
                <Approachability tooltip approachability={approachability} />
              )}
              {puzData.type === PuzzleType.PUZZLE &&
              puzData.roundId === ROUND_IDS.posthunt ? (
                <div className="approachability">
                  <Tooltip text="This puzzle is easy as pie." into>
                    <Twemoji>🥧</Twemoji>
                  </Tooltip>
                </div>
              ) : null}
            </h1>
          </div>
        )}
        <div style={{ display: "flex", gap: "0.5em", alignItems: "center" }}>
          {puzData.type !== PuzzleType.PUZZLE || isDesert ? null : (
            <Tooltip
              text={
                [ROUND_IDS.intro, ROUND_IDS.atoll, ROUND_IDS.site].includes(
                  puzData.roundId!
                )
                  ? "Show icons and highlight all pieces"
                  : "Highlight all pieces"
              }
            >
              <button
                id="world-base-highlight-button"
                onClick={(e) => {
                  const button = e.currentTarget;
                  if (button.classList.contains("focused")) {
                    button.classList.remove("focused");
                  } else {
                    button.classList.add("focused");
                  }
                }}
              >
                <Twemoji>🔦️</Twemoji>
              </button>
            </Tooltip>
          )}
          {/* TODO: allow turning this off per-puzzle */}
          {puzData.type === PuzzleType.STORY ||
          isDesert ||
          puzzleContent.content.trim() === "" ? null : (
            <Tooltip
              text={
                puzData.type === PuzzleType.PUZZLE
                  ? "Copy puzzle content to clipboard"
                  : "Copy to clipboard"
              }
              placement="bottom"
            >
              <button
                className="clipboard-button"
                onClick={(e) => {
                  Globals.handleClipboardButton(null, e.currentTarget);
                }}
                style={{ width: "max-content" }}
              >
                <Twemoji>
                  📋️{puzData.type === PuzzleType.PUZZLE && " 🔲️"}
                </Twemoji>
              </button>
            </Tooltip>
          )}
          {puzData.type !== PuzzleType.PUZZLE || isDesert ? null : (
            <Tooltip text="Copy piece contents to clipboard" placement="bottom">
              <button
                className="clipboard-button"
                onClick={(e) => {
                  Globals.handleClipboardButton(
                    document.querySelector(".piece-overlay") as HTMLElement,
                    e.currentTarget
                  );
                }}
                style={{ width: "max-content" }}
              >
                <Twemoji>📋️ 🧩️</Twemoji>
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      {isSettingCustomTitle ? (
        <Paper>
          You can give a piece a name to make it easier for your team to find
          it. Come back to change it anytime. This is not part of the puzzle.
        </Paper>
      ) : null}
    </>
  );
}

export function AdminRelatedPuzzleControls(props: {
  puzName: string;
  teamId?: string;
  readOnly?: true;
}) {
  const huntAdminScopeState = useGlobalScopeState(ScopeType.HUNT_ADMIN);

  if (huntAdminScopeState === null) return <Spinner inline />;

  const { puzName, ...otherProps } = props;
  const { puzzles } = huntAdminScopeState;
  const current = puzzles[puzName];
  const relatedPuzzles = Object.values(puzzles)
    .filter((other) => {
      if (other.puzName === current.puzName) return false;
      if (current.type === PuzzleType.PIECE && other.type === PuzzleType.PUZZLE)
        return doesPieceBelongTo(current, other);
      if (current.type === PuzzleType.PUZZLE && other.type === PuzzleType.PIECE)
        return doesPieceBelongTo(other, current);
      return (
        current.type === PuzzleType.PUZZLE &&
        other.type === PuzzleType.PUZZLE &&
        current.isMeta &&
        other.roundId === current.roundId
      );
    })
    .sort((a, b) => a.order - b.order);
  return (
    <div className="posthunt-control admin-puzzles">
      <AdminPuzzleControl puzName={puzName} {...otherProps} />
      {relatedPuzzles.map(({ puzName: otherPuzName }) => (
        <AdminPuzzleControl
          key={otherPuzName}
          puzName={otherPuzName}
          parentPuzName={puzName}
          {...otherProps}
        />
      ))}
    </div>
  );
}

function TrueColorsColorPickerImage({
  puzName,
  onPickColor,
  onClick,
}: {
  puzName: string;
  onPickColor: (color: string | null) => void;
  onClick: () => void;
}) {
  const puzzleContent = useScopeStateResolvable(ScopeType.PUZZLE_CONTENT, {
    puzName,
  });
  const [bitmapView, setBitmapView] = useState<DataView | null>(null);
  const [errored, setErrored] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const version = 0;
  const staticPuzDir =
    puzzleContent === null ? null : getPuzzleContentStaticDir(puzzleContent);

  useEffect(() => {
    if (staticPuzDir === null) return;
    (async () => {
      const res = await fetch(`${staticPuzDir}/default.bmp?${version}`);
      if (!res.ok)
        throw new Error(`Failed to get bitmap with status ${res.status}`);
      setBitmapView(new DataView(await res.arrayBuffer()));
    })().catch((err) => {
      console.error(err);
      setErrored(true);
    });
  }, [staticPuzDir]);

  const pickColor = useEvent((e: React.MouseEvent<HTMLImageElement>) => {
    if (bitmapView === null) return;
    if (imgRef.current === null) return;
    const el = imgRef.current;
    const rect = el.getBoundingClientRect();
    const xFrac = (e.pageX - rect.x) / rect.width;
    const yFrac = (e.pageY - rect.y) / rect.height;
    onPickColor(getBitmapPixelColor(bitmapView, xFrac, yFrac));
  });

  if (errored)
    return (
      <div className="true-colors-color-picker-error">
        Error loading image. Please refresh the page.
      </div>
    );

  if (
    !errored &&
    (puzzleContent === null || bitmapView === null || staticPuzDir === null)
  )
    return <LargeSpinner inline />;

  return (
    <div className="true-colors-color-picker">
      <img
        className="true-colors-color-picker-img"
        src={`${staticPuzDir}/default.png?${version}`}
        ref={imgRef}
        onMouseMove={pickColor}
        onMouseEnter={pickColor}
        onMouseLeave={() => onPickColor(null)}
        onClick={onClick}
      />
    </div>
  );
}

enum TrueColorsColorPickerCopyState {
  NONE = "none",
  COPIED = "copied",
  ERROR = "error",
}

function TrueColorsColorPicker({ puzNames }: { puzNames: string[] }) {
  const [pickedColor, setPickedColor] = useState<string | null>(null);
  const [copyState, setCopyState] = useState<TrueColorsColorPickerCopyState>(
    TrueColorsColorPickerCopyState.NONE
  );
  // Copy to clipboard needs to get the current picked color
  // non-reactively, so use a parallel ref for that.
  const pickedColorNonReactive = useRef<string | null>(null);
  const copyToClipboard = useEvent(async () => {
    (async () => {
      if (pickedColorNonReactive.current === null) return;
      await navigator.clipboard.writeText(pickedColorNonReactive.current);
      setCopyState(TrueColorsColorPickerCopyState.COPIED);
    })().catch((err) => {
      setCopyState(TrueColorsColorPickerCopyState.ERROR);
      console.error(err);
    });
  });
  const pickColor = useEvent((color: string | null) => {
    setCopyState(TrueColorsColorPickerCopyState.NONE);
    setPickedColor(color);
    pickedColorNonReactive.current = color;
  });

  const trueColorsPieces = puzNames.filter((puzName) =>
    TRUE_COLORS_PIECES_PUZ_NAMES.includes(puzName)
  );
  if (trueColorsPieces.length === 0) return null;

  const copiedText = ((): string => {
    switch (copyState) {
      case TrueColorsColorPickerCopyState.NONE:
        return "(click to copy to clipboard)";
      case TrueColorsColorPickerCopyState.COPIED:
        return "(copied!)";
      case TrueColorsColorPickerCopyState.ERROR:
        return "(copy to clipboard failed)";
    }
  })();

  return (
    <div className="true-colors-color-picker">
      <h3>Color Inspector</h3>
      <p>
        <b>
          Inspecting colors with image editing software has been found to be
          inaccurate on many systems. Please use the below gadget to retrieve
          accurate color values.
        </b>
      </p>
      <div className="true-colors-color-picker-picked-color">
        <div className="true-colors-color-picker-picked-color-text">
          Color under cursor:{" "}
          <b>{pickedColor === null ? "Not in image" : pickedColor}</b>
        </div>
        {pickedColor === null ? null : (
          <>
            <div
              className="true-colors-color-picker-preview"
              style={{
                backgroundColor: pickedColor,
              }}
            ></div>
            <div className="true-colors-color-picker-picked-color-text">
              {copiedText}
            </div>
          </>
        )}
      </div>
      <div className="true-colors-color-picker-imgs-wrapper">
        <div className="true-colors-color-picker-imgs">
          {trueColorsPieces.map((puzName) => (
            <TrueColorsColorPickerImage
              key={puzName}
              puzName={puzName}
              onPickColor={pickColor}
              onClick={copyToClipboard}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TrueColorsColorPickerForBox({ boxId }: { boxId: string }) {
  const { pieceIds } = useBox(boxId);
  const boxPieces = useBoxPieces(boxId, pieceIds ?? []);
  if (pieceIds === undefined) return <LargeSpinner inline />;
  const foundBoxPieces = boxPieces.filter((piece) => piece !== null);
  if (foundBoxPieces.length < boxPieces.length) return <LargeSpinner inline />;
  const boxPiecesById = Object.fromEntries(
    foundBoxPieces.map((piece) => [getPieceId(piece.id), piece])
  );
  const sortedPieceIds = [...pieceIds].sort((pieceId1, pieceId2) => {
    const piece1 = boxPiecesById[pieceId1];
    const piece2 = boxPiecesById[pieceId2];
    if (piece1 === undefined) throw new Error(`no piece by id ${pieceId1}`);
    if (piece2 === undefined) throw new Error(`no piece by id ${pieceId2}`);
    return piece1.row - piece2.row;
  });
  return <TrueColorsColorPicker puzNames={sortedPieceIds} />;
}

export default function PuzzleBodyDisplay() {
  const puzData = useOutletContext<TeamPuzzleSockAPIScopeState>();
  const round = useRound();
  const deps = usePuzzleBodyDependencies(puzData);
  const shouldDisplayAdmin = useShouldDisplayAdmin();
  const isSpectating = useIsAdminEnabledStore((state) => state.isSpectating);
  const [showLocked, setShowLocked] = useState(false);
  const urlPuzzleSlug = useRouteMatch("puzzle")?.params.slug;
  const urlPieceSlug = useRouteMatch("piece")?.params.slug;
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);
  const { puzName, answerNormalization, hasMultipleAnswers, type, slug } =
    puzData;

  useEffect(() => {
    if (type === PuzzleType.PIECE && urlPuzzleSlug === slug)
      setRedirectRoute({ route: "piece", params: { slug }, replace: true });
    if (type !== PuzzleType.PIECE && urlPieceSlug === slug)
      setRedirectRoute({ route: "puzzle", params: { slug }, replace: true });
  }, [type, slug, urlPuzzleSlug, urlPieceSlug, setRedirectRoute]);

  const {
    teamId,
    errataContent,
    renderCtx,
    teamAnswerableScopeState,
    isPuzzleUnlocked = null,
    isPuzzleSolved = null,
  } = deps ?? {};
  // Always return a non-undefined value to make typescript
  // check for exhaustiveness.
  const answerNormalizationNotice = ((): string | null => {
    if (answerNormalization === undefined) return null;
    switch (answerNormalization) {
      case AnswerNormalization.UPPERCASE_LETTERS_ONLY:
      case AnswerNormalization.UPPERCASE_LETTERS_OR_DIGITS_ONLY:
        return null;
      case AnswerNormalization.UPPERCASE_LETTERS_OR_SYMBOLS_ONLY:
      case AnswerNormalization.NFKD:
        return "This puzzle’s answer may contain digits, punctuation marks or other symbols.";
    }
  })();
  const showControls =
    (shouldDisplayAdmin && !isSpectating) ||
    (settings.isPosthunt && type !== PuzzleType.STORY);
  const isDesert = puzData.roundId === DESERT_ISLAND_ROUND_ID;
  const roundFontSizeScaling =
    round !== null ? (FONT_SIZE_SCALING_PER_ROUND[round.roundId] ?? 1) : 1;

  return (
    <div
      className={clsObj({
        "puzzle-page-body": true,
        "puzzle-page-body-full-width": type === PuzzleType.STORY,
      })}
    >
      {errataContent && <ErrataDisplay contents={errataContent} />}
      <PuzzlePageTitleDisplay
        puzData={puzData}
        teamAnswerableScopeState={teamAnswerableScopeState}
      />
      {!renderCtx || isPuzzleUnlocked === null ? (
        <Spinner inline />
      ) : isPuzzleUnlocked || settings.factcheckMode || showLocked ? (
        ((): ReactNode => {
          switch (puzData.type) {
            case PuzzleType.PUZZLE: {
              if (round === null)
                throw new Error("expect all puzzles to belong to a round");
              const cols = puzData.cols ?? round.boxCols;
              const rows = puzData.rows ?? round.boxRows;
              if (isDesert)
                return (
                  <div id="puzzle-content-wrapper">
                    <PuzzleContentDisplayPreloaded renderCtx={renderCtx} />
                  </div>
                );

              return (
                <>
                  <BoxMenu boxId={puzName} />
                  <TrueColorsColorPickerForBox boxId={puzName} />
                  <div
                    id="puzzle-content-scroll-wrapper"
                    style={{
                      padding: `${BASE_WRAPPER_BORDER_PX}px`,
                    }}
                  >
                    <div
                      id="puzzle-content-wrapper"
                      className={clsObj({
                        "puzzle-content-wrapper-bordered": true,
                      })}
                      style={{
                        aspectRatio: `${cols / (round.gridCols / 2)} / ${rows / round.gridRows}`,
                        width: `${BASE_INNER_WRAPPER_WIDTH_PX}px`,
                        fontSize: `${(((BASE_FONT_SIZE_PX / cols) * BASE_INNER_WRAPPER_WIDTH_PX) / BASE_OUTER_WRAPPER_WIDTH_PX) * roundFontSizeScaling}px`,
                      }}
                    >
                      <PuzzleContentDisplayPreloaded
                        renderCtx={renderCtx}
                        fontSize={`${cols}em`}
                      />
                      <div style={{}}>
                        <PieceOverlay puzData={puzData} />
                      </div>
                    </div>
                  </div>
                </>
              );
            }
            case PuzzleType.PIECE: {
              if (round === null)
                throw new Error("expect all pieces to belong to a round");
              const cols = puzData.cols ?? round.pieceCols;
              const rows = puzData.rows ?? round.pieceRows;
              return (
                <>
                  <PieceMenu boxId={null} pieceId={puzName} />
                  <TrueColorsColorPicker puzNames={[puzName]} />
                  <div
                    id="puzzle-content-scroll-wrapper"
                    style={{
                      padding: `${BASE_WRAPPER_BORDER_PX}px`,
                    }}
                  >
                    <div
                      id="puzzle-content-wrapper"
                      className="puzzle-content-wrapper-bordered"
                      style={{
                        fontSize: `${(((BASE_FONT_SIZE_PX / cols) * BASE_INNER_WRAPPER_WIDTH_PX) / BASE_OUTER_WRAPPER_WIDTH_PX) * roundFontSizeScaling}px`,
                        width: `${BASE_INNER_WRAPPER_WIDTH_PX}px`,
                        minHeight: `${(BASE_INNER_WRAPPER_WIDTH_PX / cols) * rows * (round.gridCols / 2 / round.gridRows)}px`,
                      }}
                    >
                      <PuzzleContentDisplayPreloaded renderCtx={renderCtx} />
                    </div>
                  </div>
                </>
              );
            }
            case PuzzleType.STORY:
              return (
                <>
                  <div id="puzzle-content-wrapper">
                    <PuzzleContentDisplayPreloaded renderCtx={renderCtx} />
                  </div>
                </>
              );
          }
        })()
      ) : (
        <div className="locked-puzzle-content">
          <div>
            You have not unlocked this puzzle. As{" "}
            {settings.isPosthunt ? "a postsolver" : "an admin"}, you may click
            the button below to view it. Note that answer submission and other
            interactive features may not work if the puzzle is locked.
          </div>
          <div>
            <button
              onClick={() => {
                setShowLocked(true);
              }}
            >
              Show puzzle
            </button>
          </div>
        </div>
      )}
      {answerNormalizationNotice !== null ? (
        <Paper>{answerNormalizationNotice}</Paper>
      ) : null}
      {hasMultipleAnswers === true ? (
        <Paper>This puzzle has multiple answers.</Paper>
      ) : null}
      {teamId !== undefined &&
      teamAnswerableScopeState !== undefined &&
      teamAnswerableScopeState !== null &&
      isPuzzleSolved !== null ? (
        <PreviousGuessesDisplayPreloaded
          teamId={teamId}
          teamAnswerableScopeState={teamAnswerableScopeState}
          showKeroFeedbackForm={
            // HACK GPH 2025: show Kero round feedback form by checking
            // a hardcoded puzName
            puzName === "474c7a6e" && isPuzzleSolved
          }
        />
      ) : null}
      {settings.factcheckMode && teamId !== undefined ? (
        <>
          <h1>Solution</h1>
          <SolutionBodyDisplay />
          <h1>Canned Hints</h1>
          <AdminCannedHintsDisplay puzName={puzName} teamId={teamId} />
        </>
      ) : null}
      {showControls ? (
        <div className="posthunt-controls">
          <h3>{shouldDisplayAdmin ? "Admin" : "Postsolver"} controls</h3>
          {!shouldDisplayAdmin ? (
            <p>
              These controls are for your convenience as a postsolver and were
              not part of the original hunt experience. You may access the full
              hunt controls in the <Link route="unlock">Unlocking page</Link>.
            </p>
          ) : null}
          <AdminRelatedPuzzleControls puzName={puzName} />
        </div>
      ) : null}
    </div>
  );
}
